import { ChevronRight, FileText, Folder, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  buildSkillFileTree,
  HERMES_SKILL_ROOT_FILE,
  SKILL_PACKAGE_TOP_DIRS,
  suggestNewSkillFilePath,
  type SkillFileTreeNode,
  type SkillPackageTopDir,
} from "@/lib/hermes-skills-package";

const TOP_DIR_LABELS: Record<SkillPackageTopDir, string> = {
  references: "说明",
  templates: "模板",
  examples: "示例",
};
import { cn } from "@/lib/utils";

type HermesSkillFileTreeProps = {
  paths: string[];
  selectedPath: string | null;
  canEdit: boolean;
  onSelect: (path: string) => void;
  onAddFile: (path: string) => void;
  onDeleteFile: (path: string) => void;
};

function TreeNodeRow({
  node,
  depth,
  selectedPath,
  canEdit,
  onSelect,
  onDeleteFile,
}: {
  node: SkillFileTreeNode;
  depth: number;
  selectedPath: string | null;
  canEdit: boolean;
  onSelect: (path: string) => void;
  onDeleteFile: (path: string) => void;
}) {
  if (node.kind === "dir") {
    return (
      <div className="space-y-0.5">
        <div
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-muted-foreground"
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
        >
          <Folder className="size-3.5 shrink-0" />
          {node.name}
        </div>
        {node.children.map((child) => (
          <TreeNodeRow
            key={child.kind === "file" ? child.path : child.name}
            node={child}
            depth={depth + 1}
            selectedPath={selectedPath}
            canEdit={canEdit}
            onSelect={onSelect}
            onDeleteFile={onDeleteFile}
          />
        ))}
      </div>
    );
  }

  const isSelected = selectedPath === node.path;
  const isRoot = node.path === HERMES_SKILL_ROOT_FILE;

  return (
    <div
      className="group flex items-center gap-1 pr-1"
      style={{ paddingLeft: `${depth * 12 + 8}px` }}
    >
      <button
        type="button"
        className={cn(
          "flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
          isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted/60",
        )}
        onClick={() => onSelect(node.path)}
      >
        <FileText className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate">{node.name}</span>
        {isSelected ? <ChevronRight className="ml-auto size-3 shrink-0 opacity-60" /> : null}
      </button>
      {canEdit && !isRoot ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-6 shrink-0 opacity-0 group-hover:opacity-100"
          aria-label={`删除 ${node.path}`}
          onClick={() => onDeleteFile(node.path)}
        >
          <Trash2 className="size-3 text-muted-foreground hover:text-destructive" />
        </Button>
      ) : null}
    </div>
  );
}

/** 技能包文件树（仅白名单目录；不提供 scripts/assets）。 */
export function HermesSkillFileTree({
  paths,
  selectedPath,
  canEdit,
  onSelect,
  onAddFile,
  onDeleteFile,
}: HermesSkillFileTreeProps) {
  const tree = buildSkillFileTree(paths);

  function handleAddFile(topDir: SkillPackageTopDir) {
    const nextPath = suggestNewSkillFilePath(paths, topDir);
    onAddFile(nextPath);
    onSelect(nextPath);
  }

  return (
    <div className="flex min-h-0 flex-col border-r border-border bg-muted/20">
      <div className="shrink-0 space-y-1.5 border-b border-border px-2 py-2">
        <p className="text-xs font-medium text-muted-foreground">文件</p>
        {canEdit ? (
          <div className="flex flex-wrap gap-0.5" role="group" aria-label="添加文件到目录">
            {SKILL_PACKAGE_TOP_DIRS.map((topDir) => (
              <Button
                key={topDir}
                type="button"
                variant="outline"
                size="sm"
                className="h-6 flex-1 px-1 text-[10px]"
                onClick={() => handleAddFile(topDir)}
              >
                <Plus className="mr-0.5 size-3 shrink-0" />
                {TOP_DIR_LABELS[topDir]}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-0.5 py-1">
          {tree.map((node) => (
            <TreeNodeRow
              key={node.kind === "file" ? node.path : node.name}
              node={node}
              depth={0}
              selectedPath={selectedPath}
              canEdit={canEdit}
              onSelect={onSelect}
              onDeleteFile={onDeleteFile}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
